/** @format */
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FullCalendarComponent as FullCalendar } from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core';
import { StateService } from '@uirouter/core';
import * as _ from 'lodash-es';
import { combineLatest, concat, Observable, of, Subject, switchMap, take, tap } from 'rxjs';
import { delay } from 'rxjs/operators';
import { ComponentDowngradeMappings, DowngradedComponent, getElementResizeEventObservable, ModalService, StaticMembers } from 'sis-common';
import { AppErrorHandler, CalendarSettingsModalComponent } from 'sis-components';
import { addOwnCalendarEventModalLazyOpener } from '../dialog/add-own-calendar-event-modal/add-own-calendar-event-modal-lazy-opener';
import { FullCalendarMessageService } from './full-calendar-message.service';
import { FullCalendarWebStorageService } from './full-calendar-web-storage.service';
import { FullCalendarService } from './full-calendar.service';
import { HiddenEventPreference } from './full-calendar.types';

@StaticMembers<DowngradedComponent>()
@Component({
    selector: 'app-full-calendar',
    templateUrl: './full-calendar.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class FullCalendarComponent implements OnInit, AfterViewInit {
    static downgrade: ComponentDowngradeMappings = {
        moduleName: 'student.calendar.fullCalendarNg',
        directiveName: 'appFullCalendar',
    };

    constructor(
        private fullCalendarService: FullCalendarService,
        private modalService: ModalService,
        private state: StateService,
        private fullCalendarMessageService: FullCalendarMessageService,
        private destroyRef: DestroyRef,
        private elementRef: ElementRef,
        private fullCalendarWebStorageService: FullCalendarWebStorageService,
        private appErrorHandler: AppErrorHandler,
        private cdr: ChangeDetectorRef,
    ) {}

    @ViewChild('breadcrumbsContainer') breadcrumbsContainer: ElementRef;
    @ViewChild('fullCalendar') fullCalendar: FullCalendar;
    @ViewChildren('calendarContainer') calendarContainers: QueryList<ElementRef>;

    @Output() toggleRightBar = new EventEmitter<HTMLElement>();

    calendarOptions: Observable<CalendarOptions>;
    cancelledEventsHidden: boolean;
    reFetchStudyEvents$: Subject<void> = new Subject<void>();
    calendarLoaded: Subject<void> = new Subject<void>();
    ariaLiveMessage: string | null = null;

    readonly addOwnCalendarEventModalLazyOpener = addOwnCalendarEventModalLazyOpener();

    ngOnInit() {
        this.calendarOptions = this.fullCalendarService.getOptions(
            () => this.getContentWidth(),
            () => this.fullCalendarApi,
        );
        this.cancelledEventsHidden = this.fullCalendarWebStorageService.getHiddenEventPreference().includes('cancelled');

        this.fullCalendarService.currentPeriodLabel$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(date => (date ? concat(of(null), of(date).pipe(delay(50)), of(null).pipe(delay(3000))) : of(null))),
                this.appErrorHandler.defaultErrorHandler(),
            )
            .subscribe(msg => {
                this.ariaLiveMessage = msg;
                this.cdr.markForCheck();
            });
    }

    ngAfterViewInit() {
        this.fullCalendarMessageService.goToDate$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((val: string) => this.fullCalendarApi?.gotoDate(val));

        this.fullCalendarMessageService.reFetchEvents$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.fullCalendarApi?.refetchEvents());

        this.reFetchStudyEvents$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.fullCalendarApi?.getEventSourceById('studyEventSource')?.refetch());

        this.calendarContainers.changes.pipe(tap(() => this.calendarLoaded.next())).subscribe();

        this.observeBreadcrumbsAndCalendarElement();

        /** Remove aria-pressed attribute from today, previous and next button, which are wrongly set up as toggle buttons by FullCalendar.*/
        const todayButton = document.querySelector('.fc-customTodayButton-button.fc-button.fc-button-primary');
        const prevButton = document.querySelector('.fc-prev-button.fc-button.fc-button-primary');
        const nextButton = document.querySelector('.fc-next-button.fc-button.fc-button-primary');

        todayButton?.removeAttribute('aria-pressed');
        prevButton?.removeAttribute('aria-pressed');
        nextButton?.removeAttribute('aria-pressed');
    }

    /**
     * A one time operation which waits for breadcrumbs and FullCalendar to render, then sets the calendar height accordingly.
     * If they are already rendered at the time of the ngAfterViewInit lifecycle, set the height without delay.
     * Sequential resize adjustments are handled by FullCalendar's windowResize callback
     */
    observeBreadcrumbsAndCalendarElement(): void {
        if (this.breadcrumbsContainer && this.calendarContainers?.length > 0 && this.fullCalendarApi) {
            this.fullCalendarService.setHeight(
                () => this.fullCalendarApi,
                () => this.getContentWidth(),
            );
            return;
        }
        const breadcrumbsContent: HTMLElement = this.breadcrumbsContainer.nativeElement;
        const breadcrumbsContentChanges$: Observable<ResizeObserverEntry[]> = getElementResizeEventObservable(breadcrumbsContent);
        combineLatest([this.calendarLoaded, breadcrumbsContentChanges$])
            .pipe(take(1))
            .subscribe((): void => {
                this.fullCalendarService.setHeight(
                    () => this.fullCalendarApi,
                    () => this.getContentWidth(),
                );
            });
    }

    getContentWidth(): number {
        return this.elementRef.nativeElement.parentElement.offsetWidth;
    }

    async addOwnCalendarEvent() {
        this.addOwnCalendarEventModalLazyOpener()
            .pipe(
                switchMap(ref =>
                    ref.afterClosed().pipe(
                        tap(result => {
                            if (result) {
                                this.state.reload();
                            }
                        }),
                    ),
                ),
            )
            .subscribe();
    }

    openCalendarSettings() {
        this.modalService.open(CalendarSettingsModalComponent, {});
    }

    openRightBar(button: HTMLButtonElement) {
        this.toggleRightBar.emit(button);
    }

    toggleCancelledEventVisibility() {
        const currentPreference: HiddenEventPreference = this.fullCalendarWebStorageService.getHiddenEventPreference();
        const updatedPreference: HiddenEventPreference = _.xor(currentPreference, ['cancelled']);
        this.fullCalendarWebStorageService.persistHiddenEventTypePreference(updatedPreference);

        this.reFetchStudyEvents$.next();
    }

    get fullCalendarApi() {
        return this.fullCalendar.getApi();
    }
}
